import {StatisticsDataTable, StatisticsDataTableProps} from "../StatisticsDataTable/StatisticsDataTable";
import {
  adjustFromTimeZoneSafely,
  adjustToTimeZoneSafely,
  flowMeterType,
  getRequiredObjectId,
  MeterFlowVolumeTotalEntity,
  MeterFlowVolumeTotalSearchCriteria,
  meterFlowVolumeTotalsSelector,
  meterType,
  SiteEntity,
  statisticsType,
  StatisticsType,
  TransportCategory,
  transportMethod,
  useSearchMeterFlowVolumeTotalsQuery
} from "@ots-frontend/ots-data";
import {useMemo, useState} from "react";
import {DataTable} from "../DataTable/DataTable";
import {Group, Indicator} from "@mantine/core";
import {DataTableColumn} from "mantine-datatable";
import {FormattedQuantity} from "../../text/FormattedQuantity/FormattedQuantity";
import {EntityIcon} from "../../icon/EntityIcon/EntityIcon";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";

type MeterFlowVolumeTotalsTableProps = {
  site: SiteEntity | string
  types: StatisticsType[],
  category?: TransportCategory
  regionId?: string
  fromDttm?: number
  toDttm?: number
}

const getMeterFlowVolumeTotalColumns = (types: StatisticsType[]): DataTableColumn<MeterFlowVolumeTotalEntity>[] => {
  return types.map((type, index) => ({
    accessor: "value" + index,
    width: 100,
    render: (record) => {
      if (type === statisticsType.quantity) {
        return (
          <FormattedQuantity
            value={{
              number: record.quantity,
              units: record.unitOfMeasure,
            }}
          />
        )
      } else {
        return "-"
      }
    }
  }))
}

const MeterFlowVolumeTotalsTable = (props: MeterFlowVolumeTotalsTableProps) => {
  const {
    site,
    category,
    types,
    regionId,
    fromDttm,
    toDttm,
  } = props;

  const {t} = useTranslation("common")
  const navigate = useNavigate()

  const selectMeterFlowVolumeTotals = useMemo(() => {
    return meterFlowVolumeTotalsSelector();
  }, []);

  const searchArg: MeterFlowVolumeTotalSearchCriteria = useMemo(() => {
    return {
      fromDttm: adjustToTimeZoneSafely(fromDttm)?.getTime(),
      toDttm: adjustToTimeZoneSafely(toDttm)?.getTime(),
      categories: category ? [category.code] : undefined,
      regionIds: regionId ? [regionId] : undefined,
      entityIds: [{
        id: getRequiredObjectId(site),
        type: 'SITE'
      }]
    }
  }, [site, category, regionId, fromDttm, toDttm])

  const {meterFlowVolumeTotals, isLoading} = useSearchMeterFlowVolumeTotalsQuery(searchArg, {
    selectFromResult: (result) => ({
      ...result,
      meterFlowVolumeTotals: selectMeterFlowVolumeTotals(result, undefined),
      isLoading: result.isLoading,
    })
  });

  return (
    <DataTable
      noHeader
      withColumnBorders
      records={meterFlowVolumeTotals}
      fetching={isLoading}
      rowContextMenu={{
        items: (record) => [
          {
            key: 'review',
            title: t("common.action.review", "Review"),
            onClick: () => navigate("/pipelines/meter-flows/review", {
              state: {
                siteId: record.meterObjectId,
                fromDttm: adjustFromTimeZoneSafely(record.fromDttm)?.getTime(),
                toDttm: adjustFromTimeZoneSafely(record.toDttm)?.getTime(),
              }
            })
          }
        ]
      }}
      columns={[
        {
          accessor: "meterName",
          width: 100,
          render: ({meterId, meterName, meterType: type, flowMeterType: flowType,}) => {
            const showIndicator = type === meterType.flow && flowType === flowMeterType.check
            if (showIndicator) {
              return (
                <Indicator inline size={6} color="orange">
                  <Group spacing="xs" ml={60}>
                    <EntityIcon
                      entityType="METER"
                      size="0.9em"
                    />
                    <EntityAnchor
                      value={{
                        objectId: meterId,
                        objectName: meterName,
                        objectType: "METER"
                      }}
                    />
                  </Group>
                </Indicator>
              )
            } else {
              return (
                <Group spacing="xs" ml={60}>
                  <EntityIcon
                    entityType="METER"
                    size="0.9em"
                  />
                  <EntityAnchor
                    value={{
                      objectId: meterId,
                      objectName: meterName,
                      objectType: "METER"
                    }}
                  />
                </Group>
              )
            }
          }
        },
        ...getMeterFlowVolumeTotalColumns(types),
      ]}
    />
  )
}

export type PipelineStatisticsDataTableProps = Omit<StatisticsDataTableProps, 'rateModel' | 'method'> & {
  regionId?: string
  fromDttm?: number
  toDttm?: number
}

export const PipelineStatisticsDataTable = (props: PipelineStatisticsDataTableProps) => {
  const {
    category,
    types,
    recordsPerPageOptions,
    regionId,
    fromDttm,
    toDttm,
    ...rest
  } = props;

  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  return (
    <StatisticsDataTable
      {...rest}
      method={transportMethod.pipeline}
      rateModel={'Pipeline'}
      types={types}
      category={category}
      childExpandedIds={expandedIds}
      childRowExpansion={{
        allowMultiple: true,
        expanded: {
          recordIds: expandedIds,
          onRecordIdsChange: setExpandedIds
        },
        content: ({record}) => (
          <MeterFlowVolumeTotalsTable
            site={record.id}
            category={category}
            types={types}
            regionId={regionId}
            fromDttm={fromDttm}
            toDttm={toDttm}/>
        )
      }}
    />
  );
}
